import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import { Button, Divider, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { Typography as JoyTypography } from "@mui/joy";
import DashboardCustomizeRounded from "@mui-symbols-material/w300/DashboardCustomizeRounded";
import { AreaGridLayout } from "../DashboardGridLayout";
import {
  getWidgetTypes,
  DashboardArea,
  WidgetInstance,
  WidgetConfig,
  createWidgetInstance,
} from "../DashboardWidgetConfig";
import { DashboardState, getDefaultDashboardState } from "./defaultDashboardState";
import { LOCAL_STORAGE_WIDGET_KEY } from "../../utils/DashboardWidgetLocalStoregeHelper";
import { Layout, Layouts } from "react-grid-layout";
import DashboardWidgetsModal from "../DashboardWidgetModal";
import AssetMapFullscreen from "../MapWidget/AssetMapFullscreen";
import { getUserResources } from "@dreebit-registry/vsm-microservice-components";
import { useKeycloak } from "@react-keycloak/web";
import {
  generateLeftAreaLayouts,
  generateRightAreaLayouts,
  findFirstAvailableSlot,
  reflowFromLg,
  WidgetLayoutInfo,
  LEFT_COLS,
  RIGHT_COLS,
  LEFT_ROW_HEIGHT,
  RIGHT_ROW_HEIGHT,
  DashboardBreakpoint,
} from "../../utils/generateLayouts";
import { useQuery, useMutation } from "@apollo/client";
import { jwtDecode } from "jwt-decode";
import { ProfileDashboard } from "../../graphql/queries/profileDashboard";
import { ProfileContract } from "../../graphql/queries/profileContract";
import { UpdateDashboardConfig } from "../../graphql/mutations/updateDashboardConfig";
import { isContractValid } from "../../utils/isContractValid";
import { Trans, useTranslation } from "react-i18next";
import _ from "lodash";
import { useAppName, useIndustryType } from "../../hooks/useIndustryType";
import useOwnClient from "../../hooks/useOwnClient";
import { WELCOME_BANNER_CAMPAIGN, WELCOME_BANNER_TYPE } from "../../config/welcomeCampaign";
import { shouldInjectWelcomeBanner, injectWelcomeBannerAtTop } from "../../utils/welcomeBanner";
import { canUseExchangeQuickAction, isWidgetInstanceAllowed } from "../../utils/dashboardWidgetAccess";

const BREAKPOINT_KEYS: DashboardBreakpoint[] = ['lg', 'md', 'sm'];

/**
 * Back-fill or repair per-breakpoint layouts by reflowing from `lg`.
 * A bp slot is considered stale when it's empty OR any widget's width
 * exceeds that bp's col count (symptom of an older save that cloned the
 * lg-wide layout into every bp key).
 */
const migrateLayouts = (
  layouts: Layouts,
  colsPerBp: Record<DashboardBreakpoint, number>,
  rowHeight: number
): Layouts => {
  const lg: Layout[] = layouts.lg ?? [];
  const next: Layouts = { lg, md: layouts.md ?? [], sm: layouts.sm ?? [] };
  for (const bp of BREAKPOINT_KEYS) {
    if (bp === 'lg') continue;
    const current = next[bp] ?? [];
    const cols = colsPerBp[bp];
    const isStale = current.length === 0 || current.some((l) => l.w > cols);
    if (isStale) {
      next[bp] = reflowFromLg(lg, cols, rowHeight);
    }
  }
  return next;
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const resources = getUserResources(keycloak.token, 'otto_crm');

  const isStacked = useMediaQuery('(max-width:859px)');
  const isLargeViewport = useMediaQuery('(min-width:1440px)');
  // Single source of truth for react-grid-layout's col count / layout choice.
  // We drive this from the viewport — not the grid container's width — so the
  // breakpoint fires at the same threshold that controls the outer flex ratio.
  const currentBp: DashboardBreakpoint = isStacked ? 'sm' : isLargeViewport ? 'lg' : 'md';

  const appName = useAppName();

  const roles = useMemo<string[]>(() => {
    if (!keycloak.token) return [];
    try {
      const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
      return (_.get(decoded, 'otto_access.roles', []) as string[]);
    } catch { return []; }
  }, [keycloak.token]);

  const accessLevel = useMemo<string>(() => {
    if (!keycloak.token) return '';
    try {
      const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
      return (_.get(decoded, 'otto_access.accessLevel', '') as string);
    } catch { return ''; }
  }, [keycloak.token]);

  const profileQuery = useQuery(ProfileDashboard);
  const userName = _.get(profileQuery, 'data.profile.name', "");

  const profileContractQuery = useQuery(ProfileContract);
  const contractValid = profileContractQuery.loading
    ? true
    : isContractValid(_.get(profileContractQuery, 'data.profile.client.contract'));

  // The service contact widget is a customer-facing answer — not offered to
  // users of an internal company or to identities without a company.
  const ownClient = useOwnClient();
  const hideServiceUnitWidget = !ownClient || ownClient.isServiceProvider;
  const industryType = useIndustryType();
  const canExchangeAssets = canUseExchangeQuickAction(industryType, roles);
  const widgetTypes = useMemo(
    () => getWidgetTypes(t, resources, contractValid, accessLevel, {hideServiceUnitWidget, canExchangeAssets}),
    [t, resources, contractValid, accessLevel, hideServiceUnitWidget, canExchangeAssets]
  );

  const [configLoaded, setConfigLoaded] = useState(false);
  const [leftInstances, setLeftInstances] = useState<WidgetInstance[]>([]);
  const [rightInstances, setRightInstances] = useState<WidgetInstance[]>([]);
  const [leftLayouts, setLeftLayouts] = useState<Layouts>({ lg: [], md: [], sm: [] });
  const [rightLayouts, setRightLayouts] = useState<Layouts>({ lg: [], md: [], sm: [] });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [updateDashboardConfigMutation] = useMutation(UpdateDashboardConfig);

  // Welcome-banner campaign version the user last dismissed. Kept in a ref
  // (not state) so every save call site picks it up without re-wiring.
  // undefined = never dismissed → dropped by JSON.stringify.
  const dismissedVersionRef = useRef<number | undefined>(undefined);

  const saveDashboardToGraphQL = useCallback((state: DashboardState) => {
    updateDashboardConfigMutation({
      variables: {
        input: {
          dashboardConfig: JSON.stringify({
            ...state,
            welcomeBannerDismissedVersion: dismissedVersionRef.current,
          }),
        },
      },
    }).catch(err => console.error("Failed to save dashboard config:", err));
  }, [updateDashboardConfigMutation]);

  // Initialize dashboard state from GraphQL (or fall back to localStorage / default)
  useEffect(() => {
    if (profileQuery.loading || profileContractQuery.loading) return;
    if (configLoaded) return;

    const rawConfig = _.get(profileQuery, 'data.profile.dashboardConfig', null) as string | null;
    let parsedState: DashboardState | null = null;

    // 1. Try GraphQL-persisted config
    if (rawConfig) {
      try { parsedState = JSON.parse(rawConfig) as DashboardState; } catch { /* ignore */ }
    }

    // 2. Try new localStorage key (users who saved before this migration)
    if (!parsedState) {
      try {
        const ls = localStorage.getItem('dashboardState');
        if (ls) parsedState = JSON.parse(ls) as DashboardState;
      } catch { /* ignore */ }
    }

    // 3. Try legacy localStorage key (pre-DashboardState format)
    if (!parsedState) {
      try {
        const old = localStorage.getItem(LOCAL_STORAGE_WIDGET_KEY);
        if (old) {
          const op = JSON.parse(old);
          if (op.widgetIds) {
            parsedState = {
              leftInstances: op.widgetIds.map((id: string) => ({ instanceId: id, type: id })),
              rightInstances: [],
              leftLayouts: { lg: [], md: [], sm: [] },
              rightLayouts: { lg: [], md: [], sm: [] },
            };
          }
        }
      } catch { /* ignore */ }
    }

    const base = parsedState ?? getDefaultDashboardState(roles, widgetTypes);

    // 4. Permission filter — remove unauthorized widgets (or configured options) and their layout entries
    const filterLayouts = (layouts: Layouts, ids: Set<string>): Layouts => ({
      lg: (layouts.lg || []).filter(l => ids.has(l.i)),
      md: (layouts.md || []).filter(l => ids.has(l.i)),
      sm: (layouts.sm || []).filter(l => ids.has(l.i)),
    });

    const filteredLeft = base.leftInstances.filter(i => isWidgetInstanceAllowed(i, widgetTypes));
    const filteredRight = base.rightInstances.filter(i => isWidgetInstanceAllowed(i, widgetTypes));
    const cleanedLeftLayouts = filterLayouts(base.leftLayouts, new Set(filteredLeft.map(i => i.instanceId)));
    const cleanedRightLayouts = filterLayouts(base.rightLayouts, new Set(filteredRight.map(i => i.instanceId)));

    // Back-fill any missing per-breakpoint layouts from the lg layout so existing
    // users get a sensible stacked layout on smaller viewports without a fresh save.
    const migratedLeftLayouts = migrateLayouts(cleanedLeftLayouts, LEFT_COLS, LEFT_ROW_HEIGHT);
    const migratedRightLayouts = migrateLayouts(cleanedRightLayouts, RIGHT_COLS, RIGHT_ROW_HEIGHT);

    // 4b. Welcome banner: every user gets it at the top (new users, existing
    // users without it, and dismissers of an older campaign). Skipped while
    // the instance is on the board or the current campaign was dismissed.
    dismissedVersionRef.current = parsedState?.welcomeBannerDismissedVersion;

    let finalLeftInstances = filteredLeft;
    let finalLeftLayouts = migratedLeftLayouts;
    const bannerInjected = shouldInjectWelcomeBanner(
      filteredLeft,
      dismissedVersionRef.current,
      WELCOME_BANNER_CAMPAIGN.version
    );
    if (bannerInjected) {
      ({ instances: finalLeftInstances, layouts: finalLeftLayouts } =
        injectWelcomeBannerAtTop(filteredLeft, migratedLeftLayouts));
    }

    setLeftInstances(finalLeftInstances);
    setRightInstances(filteredRight);
    setLeftLayouts(finalLeftLayouts);
    setRightLayouts(migratedRightLayouts);
    setConfigLoaded(true);

    // 5. Auto-save to GraphQL if migrating from localStorage, if widgets were
    // filtered out, or if the welcome banner was injected into a saved config.
    // A fresh default state (parsedState === null) is deliberately NOT saved:
    // the defaults stay role-dynamic until the user interacts, and the banner
    // is simply re-injected on every load until dismissed.
    const fromLocalStorage = !rawConfig && parsedState !== null;
    const wasFiltered =
      filteredLeft.length !== base.leftInstances.length ||
      filteredRight.length !== base.rightInstances.length;

    if (fromLocalStorage || wasFiltered || (bannerInjected && parsedState !== null)) {
      saveDashboardToGraphQL({
        leftInstances: finalLeftInstances,
        rightInstances: filteredRight,
        leftLayouts: finalLeftLayouts,
        rightLayouts: migratedRightLayouts,
      });
    }

    // 6. Clear old localStorage keys after migration
    localStorage.removeItem('dashboardState');
    localStorage.removeItem(LOCAL_STORAGE_WIDGET_KEY);
  }, [profileQuery.loading, profileContractQuery.loading, configLoaded, roles, widgetTypes, saveDashboardToGraphQL]);

  // Generate initial layouts if empty (after config is loaded)
  useEffect(() => {
    if (!configLoaded) return;
    if ((!leftLayouts.lg || leftLayouts.lg.length === 0) && leftInstances.length > 0) {
      const widgetInfos: WidgetLayoutInfo[] = leftInstances.map(instance => {
        const widget = widgetTypes.find(w => w.type === instance.type);
        return { id: instance.instanceId, width: widget?.width || 2, height: widget?.height };
      });
      setLeftLayouts(generateLeftAreaLayouts(widgetInfos));
    }
  }, [configLoaded, leftInstances, leftLayouts.lg, widgetTypes]);

  useEffect(() => {
    if (!configLoaded) return;
    if ((!rightLayouts.lg || rightLayouts.lg.length === 0) && rightInstances.length > 0) {
      const widgetInfos: WidgetLayoutInfo[] = rightInstances.map(instance => {
        const widget = widgetTypes.find(w => w.type === instance.type);
        return { id: instance.instanceId, width: widget?.width || 2, height: widget?.height };
      });
      setRightLayouts(generateRightAreaLayouts(widgetInfos));
    }
  }, [configLoaded, rightInstances, rightLayouts.lg, widgetTypes]);

  // Filter rendered instances to hide unauthorized widgets without discarding saved state
  const validLeftInstances = useMemo(
    () => leftInstances.filter(i => isWidgetInstanceAllowed(i, widgetTypes)),
    [leftInstances, widgetTypes]
  );
  const validRightInstances = useMemo(
    () => rightInstances.filter(i => isWidgetInstanceAllowed(i, widgetTypes)),
    [rightInstances, widgetTypes]
  );

  // For singleton widgets, filter out already-added ones
  const availableWidgets = widgetTypes.filter((widget) => {
    if (widget.configurable) return true;
    const allInstances = [...leftInstances, ...rightInstances];
    return !allInstances.some(instance => instance.type === widget.type);
  });

  /**
   * Add a new widget instance to the dashboard and save to GraphQL.
   */
  const handleAddWidget = useCallback((widgetType: string, config?: WidgetConfig) => {
    const widget = widgetTypes.find(w => w.type === widgetType);
    if (!widget) return;

    // The welcome banner is a hero: re-adding it always places it at the top
    // and shifts everything else down (findFirstAvailableSlot would drop a
    // full-width widget to the bottom). The dismissal stamp is deliberately
    // kept — presence wins while the banner is on the board.
    if (widgetType === WELCOME_BANNER_TYPE) {
      if (leftInstances.some(i => i.type === WELCOME_BANNER_TYPE)) return;
      const { instances, layouts } = injectWelcomeBannerAtTop(leftInstances, leftLayouts);
      setLeftInstances(instances);
      setLeftLayouts(layouts);
      saveDashboardToGraphQL({
        leftInstances: instances,
        rightInstances,
        leftLayouts: layouts,
        rightLayouts,
      });
      return;
    }

    const newInstance = createWidgetInstance(widget, config);
    const area: DashboardArea = widget.area;
    const currentInstances = area === 'left' ? leftInstances : rightInstances;
    const currentLayouts = area === 'left' ? leftLayouts : rightLayouts;

    if (!widget.configurable) {
      const exists = currentInstances.some(i => i.type === widgetType);
      if (exists) return;
    }

    const colsPerBp = area === 'left' ? LEFT_COLS : RIGHT_COLS;
    const rowHeight = area === 'left' ? LEFT_ROW_HEIGHT : RIGHT_ROW_HEIGHT;
    const widgetHeight = rowHeight * (widget.height || 1);

    // Compute placement independently for each breakpoint so the per-bp
    // customizations stay coherent and widths clamp to the narrower grids.
    const appendForBp = (bp: DashboardBreakpoint): Layout => {
      const cols = colsPerBp[bp];
      const w = Math.min(widget.width, cols);
      const layoutForBp = currentLayouts[bp] ?? [];
      const { x, y } = findFirstAvailableSlot(currentLayouts, w, widgetHeight, cols, layoutForBp);
      return { i: newInstance.instanceId, x, y, w, h: widgetHeight };
    };

    const nextLayoutsForArea: Layouts = {
      lg: [...(currentLayouts.lg || []), appendForBp('lg')],
      md: [...(currentLayouts.md || []), appendForBp('md')],
      sm: [...(currentLayouts.sm || []), appendForBp('sm')],
    };

    const nextLeftInstances = area === 'left' ? [...leftInstances, newInstance] : leftInstances;
    const nextRightInstances = area === 'right' ? [...rightInstances, newInstance] : rightInstances;
    const nextLeftLayouts = area === 'left' ? nextLayoutsForArea : leftLayouts;
    const nextRightLayouts = area === 'right' ? nextLayoutsForArea : rightLayouts;

    setLeftInstances(nextLeftInstances);
    setRightInstances(nextRightInstances);
    setLeftLayouts(nextLeftLayouts);
    setRightLayouts(nextRightLayouts);

    saveDashboardToGraphQL({
      leftInstances: nextLeftInstances,
      rightInstances: nextRightInstances,
      leftLayouts: nextLeftLayouts,
      rightLayouts: nextRightLayouts,
    });
  }, [widgetTypes, leftInstances, rightInstances, leftLayouts, rightLayouts, saveDashboardToGraphQL]);

  /**
   * Remove a widget instance from the dashboard and save to GraphQL.
   */
  const handleRemoveWidget = useCallback((instanceId: string) => {
    // Removing the welcome banner counts as dismissing the current campaign
    // (covers both the widget ⋮ menu and the picker modal's remove toggle).
    if (instanceId === WELCOME_BANNER_TYPE) {
      dismissedVersionRef.current = WELCOME_BANNER_CAMPAIGN.version;
    }

    const inLeft = leftInstances.some(i => i.instanceId === instanceId);
    const inRight = rightInstances.some(i => i.instanceId === instanceId);

    const nextLeftInstances = inLeft ? leftInstances.filter(i => i.instanceId !== instanceId) : leftInstances;
    const nextRightInstances = inRight ? rightInstances.filter(i => i.instanceId !== instanceId) : rightInstances;
    const nextLeftLayouts = inLeft ? {
      lg: (leftLayouts.lg || []).filter(l => l.i !== instanceId),
      md: (leftLayouts.md || []).filter(l => l.i !== instanceId),
      sm: (leftLayouts.sm || []).filter(l => l.i !== instanceId),
    } : leftLayouts;
    const nextRightLayouts = inRight ? {
      lg: (rightLayouts.lg || []).filter(l => l.i !== instanceId),
      md: (rightLayouts.md || []).filter(l => l.i !== instanceId),
      sm: (rightLayouts.sm || []).filter(l => l.i !== instanceId),
    } : rightLayouts;

    setLeftInstances(nextLeftInstances);
    setRightInstances(nextRightInstances);
    setLeftLayouts(nextLeftLayouts);
    setRightLayouts(nextRightLayouts);

    saveDashboardToGraphQL({
      leftInstances: nextLeftInstances,
      rightInstances: nextRightInstances,
      leftLayouts: nextLeftLayouts,
      rightLayouts: nextRightLayouts,
    });
  }, [leftInstances, rightInstances, leftLayouts, rightLayouts, saveDashboardToGraphQL]);

  /**
   * Toggle a singleton widget (add if not present, remove if present).
   * For configurable widgets, always adds a new instance.
   */
  const handleToggleWidget = useCallback((widgetType: string, config?: WidgetConfig) => {
    const widget = widgetTypes.find(w => w.type === widgetType);
    if (!widget) return;

    if (widget.configurable) {
      handleAddWidget(widgetType, config);
      return;
    }

    const allInstances = [...leftInstances, ...rightInstances];
    const existingInstance = allInstances.find(i => i.type === widgetType);

    if (existingInstance) {
      handleRemoveWidget(existingInstance.instanceId);
    } else {
      handleAddWidget(widgetType);
    }
  }, [widgetTypes, leftInstances, rightInstances, handleAddWidget, handleRemoveWidget]);

  // Drag-stop handlers — fire only when user finishes dragging a widget
  const dragJustCompleted = useRef(false);

  const handleLeftDragStop = useCallback(() => {
    dragJustCompleted.current = true;
  }, []);

  const handleRightDragStop = useCallback(() => {
    dragJustCompleted.current = true;
  }, []);

  const handleLeftLayoutChange = useCallback((layout: Layout[]) => {
    const nextLayouts: Layouts = { ...leftLayouts, [currentBp]: layout };
    setLeftLayouts(nextLayouts);
    if (dragJustCompleted.current) {
      dragJustCompleted.current = false;
      saveDashboardToGraphQL({ leftInstances, rightInstances, leftLayouts: nextLayouts, rightLayouts });
    }
  }, [currentBp, leftLayouts, leftInstances, rightInstances, rightLayouts, saveDashboardToGraphQL]);

  const handleRightLayoutChange = useCallback((layout: Layout[]) => {
    const nextLayouts: Layouts = { ...rightLayouts, [currentBp]: layout };
    setRightLayouts(nextLayouts);
    if (dragJustCompleted.current) {
      dragJustCompleted.current = false;
      saveDashboardToGraphQL({ leftInstances, rightInstances, leftLayouts, rightLayouts: nextLayouts });
    }
  }, [currentBp, rightLayouts, leftInstances, rightInstances, leftLayouts, saveDashboardToGraphQL]);

  const allWidgetTypesOnDashboard = [
    ...leftInstances.map(i => i.type),
    ...rightInstances.map(i => i.type),
  ];

  const leftFlex = isLargeViewport ? 4 : 2;
  const rightFlex = 2;
  const leftAreaSx = isStacked ? { width: "100%", minWidth: 0 } : { flex: leftFlex, minWidth: 0 };
  const rightAreaSx = isStacked ? { width: "100%", minWidth: 0 } : { flex: rightFlex, minWidth: 0 };
  const dividerProps = isStacked
    ? { orientation: "horizontal" as const, sx: { my: "21px" } }
    : { orientation: "vertical" as const, flexItem: true, sx: { mx: "21px" } };

  return (
    <div className="dashboard" style={{ padding: "13px 21px 21px", width: "100%", height: "100%", overflowY: "auto", boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Box sx={{ flex: 1, ml: "16px" }}>
          {profileQuery.loading ? (
            <Skeleton variant="text" width={300} height={32} />
          ) : (
            <JoyTypography level="titleMediumLight">
              <Trans
                t={t}
                i18nKey="Welcome back to <1>{{appName}}</1>, {{userName}}!"
                values={{ appName, userName }}
                components={{ 1: <JoyTypography level="titleMedium" component="span" /> }}
              />
            </JoyTypography>
          )}
        </Box>
        <Button
          variant="outlined"
          onClick={() => setIsModalOpen(true)}
          sx={{ height: "42px", width: "42px", marginRight: 1 }}
        >
          <DashboardCustomizeRounded />
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isStacked ? "column-reverse" : "row",
          width: "100%",
          minHeight: 400,
        }}
      >
        {!configLoaded ? (
          <>
            <Box sx={leftAreaSx}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Box>
            <Divider {...dividerProps} />
            <Box sx={rightAreaSx}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Box>
          </>
        ) : (
          <>
            {/* Left Area - General Dashboard */}
            <Box sx={leftAreaSx}>
              <AreaGridLayout
                area="left"
                currentBp={currentBp}
                widgetTypes={widgetTypes}
                instances={validLeftInstances}
                layouts={leftLayouts}
                onLayoutChange={handleLeftLayoutChange}
                onDragStop={handleLeftDragStop}
                onDeleteWidget={handleRemoveWidget}
              />
            </Box>

            {/* Full-height divider between areas (vertical desktop / horizontal stacked) */}
            <Divider {...dividerProps} />

            {/* Right Area - Personal Space */}
            <Box sx={rightAreaSx}>
              {validRightInstances.length > 0 ? (
                <AreaGridLayout
                  area="right"
                  currentBp={currentBp}
                  widgetTypes={widgetTypes}
                  instances={validRightInstances}
                  layouts={rightLayouts}
                  onLayoutChange={handleRightLayoutChange}
                  onDragStop={handleRightDragStop}
                  onDeleteWidget={handleRemoveWidget}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                    border: "2px dashed #e0e0e0",
                    borderRadius: 2,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">
                    {t('Add personal widgets here')}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <DashboardWidgetsModal
        widgetTypes={widgetTypes}
        open={isModalOpen}
        availableWidgets={availableWidgets}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleToggleWidget}
        onRemoveInstance={handleRemoveWidget}
        widgetIds={allWidgetTypesOnDashboard}
        instances={[...leftInstances, ...rightInstances]}
      />
      {/* ?map=fullscreen — offered whenever the map widget is, also when it isn't placed on the dashboard */}
      {widgetTypes.some((widgetType) => widgetType.type === "assetMap") && <AssetMapFullscreen />}
    </div>
  );
};

export default Dashboard;
